// @flow

/* eslint-disable ta-code-conventions/has-matching-storydoc, ta-code-conventions/has-matching-test */

import Logging from '@ta/common.logging';
import type { Fetcher, FetcherResults } from '@ta/common.typeahead';

/** See GeoTheme.java for the enumerated options. Add them to this union type as you need them. */
type GeoThemeType = 0; // POPULAR_DESTINATIONS

type Props = {|
  /** The theme type of geos to fetch */
  theme: GeoThemeType,

  /** The maximum number of results to include (defaults to 10) */
  limit?: number,
|};

/** The fetcher ID */
export const ID = 'geotheme';

/**
 * A typeahead fetcher for geo themes, e.g. popular destinations. See GeoTheme.java for more examples.
 */
class GeoThemeFetcher implements Fetcher {
  id: string = ID;
  theme: number = 0;
  limit: number = 10;
  results: FetcherResults = null;
  inFlight: boolean = false;

  constructor({ theme, limit }: Props) {
    if (theme) {
      this.theme = theme;
    }
    if (limit) {
      this.limit = limit;
    }
  }

  getResults(): FetcherResults {
    return this.results;
  }

  fetch(term: string, context: ?{ [string]: any }, onChange: () => mixed): void {
    // we don't actually care about the term here. It should be the same result regardless so only fetch once and ignore
    // multiple requests
    if (this.inFlight || this.results !== null) {
      return;
    }

    const params = new URLSearchParams({
      geoThemeId: String(this.theme),
      limit: String(this.limit),
    });

    this.inFlight = true;
    fetch(`/data/1.0/destinations/bundle?${params.toString()}`, {
      method: 'GET',
      credentials: 'same-origin',
    })
      .then(response => {
        if (response.ok) {
          return response;
        }
        throw Error(response.statusText);
      })
      .then(response => response.json())
      .then(results => {
        this.results = results.geos;
        this.inFlight = false;
        onChange();
      })
      .catch(error => {
        Logging.error('Could not fetch geo theme typeahead results.', error);
        this.results = [];
        this.inFlight = false;
        onChange();
      });
  }
}

export default GeoThemeFetcher;
